type WebSentry = typeof import("@sentry/react");

const sentryDsn = (
  import.meta.env.VITE_SENTRY_DSN as string | undefined
)?.trim();

let sentryModulePromise: Promise<WebSentry | undefined> | undefined;

/**
 * Keep Sentry out of the critical path and out of local builds without a DSN.
 * The SDK is loaded only when the deployment explicitly enables web
 * observability.
 */
export function initWebSentry(): Promise<WebSentry | undefined> {
  if (typeof window === "undefined" || !sentryDsn) {
    return Promise.resolve(undefined);
  }

  if (!sentryModulePromise) {
    sentryModulePromise = import("@sentry/react")
      .then((Sentry) => {
        Sentry.init({
          dsn: sentryDsn,
          environment:
            (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined)?.trim() ||
            import.meta.env.MODE,
          sendDefaultPii: false,
          tracesSampleRate: 0.05,
          replaysSessionSampleRate: 0,
          replaysOnErrorSampleRate: 0,
          beforeSend: sanitizeWebSentryEvent,
        });
        return Sentry;
      })
      .catch(() => undefined);
  }

  return sentryModulePromise;
}

export function sanitizeWebSentryEvent<T extends {
  request?: { url?: string };
}>(event: T): T {
  const requestUrl = event.request?.url;
  if (!requestUrl) return event;

  try {
    const parsed = new URL(requestUrl);
    event.request = {
      ...event.request,
      url: `${parsed.origin}${parsed.pathname}`,
    };
  } catch {
    event.request = {
      ...event.request,
      url: undefined,
    };
  }

  return event;
}

export function captureWebException(
  error: unknown,
  context: Record<string, string> = {},
) {
  void initWebSentry().then((Sentry) => {
    if (!Sentry) return;

    Sentry.withScope((scope) => {
      for (const [key, value] of Object.entries(context)) {
        scope.setTag(key, value);
      }
      Sentry.captureException(error);
    });
  });
}
